// pipeline-engines.ts — billed-/video-/tekst-motorer (openAiImage, kieImage, geminiImage, geminiVideo, geminiText).
// AUTO: del af pipeline (splittet fra pipeline.ts). Klassisk script — deler global scope.
// The canvas a caller asks for. Studio passes its own format and 1K; without it
// the engines read the Packshot tab's output settings, as they always have.
interface ImageShape {
    aspect?: string;
    resolution?: string;
}

function _currentOutputAspect(): string {
    try {
        if (typeof state !== 'undefined' && state?.billede?.outputAspect) {
            return state.billede.outputAspect;
        }
    } catch (_) { /* noop */ }
    return '1:1';
}

function _aspectPromptHint(ratio: string): string {
    const map: Record<string, string> = {
        '16:9': 'OUTPUT FORMAT: 16:9 widescreen (landscape). Compose the subject centered with generous horizontal framing.',
        '1:1':  'OUTPUT FORMAT: 1:1 square. Compose the subject centered in a square frame with even margins.',
        '3:4':  'OUTPUT FORMAT: 3:4 portrait. Compose the subject vertically centered, head-to-hem visible, balanced top/bottom margins.',
        '4:5':  'OUTPUT FORMAT: 4:5 portrait. Compose the subject vertically centered with even margins (Instagram portrait standard).',
        '9:16': 'OUTPUT FORMAT: 9:16 vertical (mobile). Compose the subject in a tall frame — full garment visible, minimal side margins.'
    };
    return map[ratio] || map['1:1'];
}

interface GeminiPart {
    text?: string;
    inlineData?: { mimeType: string; data: string };
}

interface GeminiCandidate {
    content?: { parts?: GeminiPart[] };
}

interface GeminiResponse {
    candidates?: GeminiCandidate[];
}

// ── OpenAI: Image fallback ──────────────────────────────────

function _openAiSize(ratio: string): string {
    const map: Record<string, string> = {
        '16:9': '1536x864',
        '1:1':  '1024x1024',
        '3:4':  '768x1024',
        '4:5':  '1024x1280',
        '9:16': '576x1024'
    };
    return map[ratio] || '1024x1024';
}

// gpt-image-2 only supports specific sizes — map our aspect ratios accordingly.
function _gptImageSize(ratio: string): string {
    const map: Record<string, string> = {
        '16:9': '1536x1024',
        '1:1':  '1024x1024',
        '3:4':  '1024x1536',
        '4:5':  '1024x1536',
        '9:16': '1024x1536'
    };
    return map[ratio] || '1024x1024';
}

// OpenAI gpt-image-2 image generation with optional image references.
// Calls our server proxy at /api/openai/image (which rebuilds multipart and forwards
// to OpenAI). Direct browser calls to OpenAI's images endpoints fail with
// "Failed to fetch" due to CORS — the proxy avoids that and keeps the key server-side.
async function openAiImage(prompt: string, imgFiles: (File | null)[] = [], onLog?: (msg: string) => void, shape: ImageShape = {}): Promise<ImageResult> {
    const validFiles = imgFiles.filter((f): f is File => f !== null);
    const isMultimodal = validFiles.length > 0;
    const targetAspect = shape.aspect || _currentOutputAspect();
    const size = _gptImageSize(targetAspect);
    
    const log = onLog || ((msg: string) => { appendLog(msg); publishLookStage(msg); });

    log(`  🤖 OpenAI gpt-image-1${isMultimodal ? ` med ${validFiles.length} ref-billed(er)` : ''} (${size})...`);

    // Base64-encode all reference images so we can send them as JSON
    const encodedImages = await Promise.all(validFiles.map(async (file) => ({
        name: file.name || 'input.png',
        mime: file.type || 'image/png',
        data: await fileToBase64(file)
    })));

    // Use authFetch (defined globally in creator.html) so the Supabase Bearer token
    // gets attached — the /api/* routes are guarded by the auth middleware on the
    // server and would otherwise return 401 "Ikke logget ind". Fall back to plain
    // fetch if authFetch isn't available (e.g. running outside the creator page).
    const _authFetch = (window as any).authFetch as (typeof fetch) | undefined;
    const fetchFn = _authFetch || fetch;
    const res = await fetchFn('/api/openai/image', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            // OpenAI's image-generation model is named `gpt-image-1`. Older code
            // used `gpt-image-2` which the API rejects with 4xx, causing every
            // OpenAI call to silently fall back to Gemini.
            model: 'gpt-image-1',
            prompt: prompt,
            size: size,
            quality: 'high',
            n: 1,
            images: encodedImages
        })
    });

    if (!res.ok) {
        const err = await res.text();
        throw new Error(`OpenAI proxy ${res.status}: ${redactSecrets(err).substring(0, 300)}`);
    }

    const j = await res.json();
    const b64 = j.data?.[0]?.b64_json;
    if (!b64) throw new Error('OpenAI returned no image');

    log('  ✓ OpenAI gpt-image-1 generated the image');
    return { data: b64, mime: 'image/png', engine: 'openai', model: 'gpt-image-1' };
}

// KIE.ai — Gemini-modeller (nano-banana-pro = Gemini 3 Pro Image) gennem KIE's
// konto. Server-proxyen (/api/kie/image) håndterer det asynkrone upload-/poll-flow
// og returnerer base64, så pipelinen ser ét enkelt synkront kald.
async function kieImage(prompt: string, imgFiles: (File | null)[] = [], onLog?: (msg: string) => void, shape: ImageShape = {}): Promise<ImageResult> {
    const validFiles = imgFiles.filter((f): f is File => f !== null);
    const targetAspect = shape.aspect || _currentOutputAspect();

    const s = (state as any) || {};
    const billede = s.billede || {};
    const resolution = String(shape.resolution || billede.outputResolution || '2k');
    const ft = (billede.outputFileType
        || (Array.isArray(billede.outputFileTypes) ? billede.outputFileTypes[0] : null)
        || 'png');

    const log = onLog || ((msg: string) => { appendLog(msg); publishLookStage(msg); });

    log(`  🤖 KIE Nano-Banana-Pro${validFiles.length ? ` med ${validFiles.length} ref-billed(er)` : ''} (${resolution.toUpperCase()})...`);

    const encodedImages = await Promise.all(validFiles.map(async (file) => ({
        name: file.name || 'input.png',
        mime: file.type || 'image/png',
        data: await fileToBase64(file)
    })));

    const _authFetch = (window as any).authFetch as (typeof fetch) | undefined;
    const fetchFn = _authFetch || fetch;
    const res = await fetchFn('/api/kie/image', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            model: 'nano-banana-pro',
            prompt,
            aspect_ratio: targetAspect,
            resolution,
            output_format: ft,
            images: encodedImages
        })
    });

    if (!res.ok) {
        const err = await res.text();
        throw new Error(`KIE proxy ${res.status}: ${redactSecrets(err).substring(0, 300)}`);
    }

    const j = await res.json() as { data?: string; mime?: string };
    if (!j.data) throw new Error('KIE returned no image');

    log('  ✓ KIE generated the image');
    return { data: j.data, mime: j.mime || 'image/png', engine: 'kie', model: 'nano-banana-pro' };
}

interface GeminiImageOpts {
    preferOpenAi?: boolean; // per-call override of USE_OPENAI_PRIMARY
    preferKie?: boolean;    // per-call override; tager forrang hvis sat
    onLog?: (msg: string) => void;
    temperature?: number;   // opt-in: lower = more deterministic/faithful (images default to 0.25)
    seed?: number;          // opt-in: fixed seed — LIVE-TEST per model first (a 400 triggers silent fallback)
    aspect?: string;        // per-call canvas (e.g. '4:5'); default: the Packshot tab's outputAspect
    resolution?: string;    // per-call size ('1k' | '2k' | '4k'); default: the Packshot tab's outputResolution
    fit?: boolean;          // false: return the engine's own pixels, the caller fits them itself
    promptAsIs?: boolean;   // true: send the prompt exactly as written, without the format line
}

// Runs the canvas post-process (fitImageToOutputAspect) but PRESERVES the engine/model
// provenance fields on the result, so we can record which engine actually produced each
// image (the fit helper itself only returns { data, mime }).
async function fitWithMeta(raw: ImageResult, log?: (msg: string) => void): Promise<ImageResult> {
    if (typeof fitImageToOutputAspect === 'function') {
        try {
            const fitted = await fitImageToOutputAspect(raw.data, raw.mime);
            return { ...fitted, engine: raw.engine, model: raw.model };
        } catch (_e) {
            if (log) log('  ⚠ Could not post-process format — using raw output');
            return raw;
        }
    }
    return raw;
}

async function geminiImage(prompt: string, imgFiles: (File | null)[] = [], opts: GeminiImageOpts = {}): Promise<ImageResult> {
    // ─── Engine-valg: explicit opts vinder, ellers læs fra app-state ─────────
    const stateModel = ((state as any)?.billede?.outputModel || '') as string;
    const useKie = (opts.preferKie === true) || (opts.preferKie === undefined && stateModel === 'kie');

    const log = opts.onLog || ((msg: string) => { appendLog(msg); publishLookStage(msg); });
    const shape: ImageShape = { aspect: opts.aspect, resolution: opts.resolution };
    const finish = (raw: ImageResult): Promise<ImageResult> => opts.fit === false ? Promise.resolve(raw) : fitWithMeta(raw, log);

    // Saml fejl fra HVER motor undervejs, så slutfejlen viser hele kæden
    // ("OpenAI: ... / KIE: ... / Gemini-3: ... / Gemini-2.5: ..."). Det fjerner
    // forvirringen om "hvorfor ser jeg en Gemini-fejl når jeg valgte GPT".
    const engineErrors: string[] = [];
    const recordErr = (engine: string, e: unknown) => {
        const msg = redactSecrets((e as Error)?.message || String(e)).substring(0, 200);
        engineErrors.push(`${engine}: ${msg}`);
        log(`  ⚠ ${engine} failed (${msg.substring(0, 80)})`);
    };

    // ─── KIE primary path ────────────────────────────────────
    let kieFailedFallback = false;
    if (useKie) {
        try {
            const raw = await kieImage(prompt, imgFiles, log, shape);
            return await finish(raw);
        } catch (e) {
            recordErr('KIE (primary)', e);
            log('Falling back to OpenAI');
            kieFailedFallback = true;
        }
    }

    // ─── OpenAI primary path ─────────────────────────────────
    const useOpenAi = kieFailedFallback || (opts.preferOpenAi ?? USE_OPENAI_PRIMARY) || stateModel === 'openai';
    let openAiFailedFallback = false;
    if (useOpenAi) {
        try {
            const raw = await openAiImage(prompt, imgFiles, log, shape);
            return await finish(raw);
        } catch (e) {
            recordErr(stateModel === 'openai' ? 'OpenAI (your choice)' : 'OpenAI', e);
            openAiFailedFallback = true;
        }
    }

    // ─── KIE fallback path ───────────────────────────────────
    if (openAiFailedFallback && !useKie) {
        try {
            log('Falling back to KIE');
            const raw = await kieImage(prompt, imgFiles, log, shape);
            return await finish(raw);
        } catch (e) {
            recordErr('KIE (fallback)', e);
            log('Falling back to Gemini');
        }
    } else if (openAiFailedFallback) {
        log('Falling back to Gemini');
    }

    // Bind error-collector to the outer closure so the Gemini-direct loop below
    // (a nested IIFE) can push into the same array.
    (geminiImage as any).__engineErrors = engineErrors;

    const parts: GeminiPart[] = [];
    for (const f of imgFiles) {
        if (f) {
            parts.push({ inlineData: { mimeType: getMime(f), data: await fileToBase64(f) } });
        }
    }

    // Tilføj aspect-hint til prompten
    const targetAspect = opts.aspect || _currentOutputAspect();
    const finalPrompt = opts.promptAsIs ? prompt : prompt + '\n\n' + _aspectPromptHint(targetAspect);
    parts.push({ text: finalPrompt });

    // Native aspect ratio + resolution for Gemini 3 (Pro + 3.1 Flash). For 2.5
    // Flash Image (the last fallback) imageSize is ignored, only aspectRatio applies.
    // Map our lowercase state ('1k'/'2k'/'4k') to the API's uppercase form ('1K'/'2K'/'4K').
    const _resRaw = opts.resolution || (state && (state as any).billede && (state as any).billede.outputResolution) || '2k';
    const _imageSize = String(_resRaw).toUpperCase().replace('0.5K', '512');

    const raw: ImageResult = await (async () => {
        // Try each image model before falling back to KIE
        for (let mi = 0; mi < IMAGE_MODELS.length; mi++) {
            const model = IMAGE_MODELS[mi];
            try {
                return await withRetry<ImageResult>(async () => {
                    // Build generationConfig — include imageConfig only for Gemini 3 family.
                    // Gemini 2.5 Flash Image does NOT accept any image-shape config:
                    //   - `imageSize` is rejected
                    //   - `response_format.image.aspect_ratio` is the wrong API path
                    //     (that field is for structured-output JSON schemas, not images)
                    //     and triggers `400 Invalid value at
                    //     generation_config.response_format.image.aspect_ratio`.
                    // For 2.5 we rely on (a) the OUTPUT FORMAT hint already appended to
                    // the prompt by `_aspectPromptHint`, and (b) post-process via
                    // `fitImageToOutputAspect` to land on exact pixels.
                    const isGemini3 = model.startsWith('gemini-3');
                    const generationConfig: any = { responseModalities: ['TEXT', 'IMAGE'] };
                    if (isGemini3) {
                        generationConfig.imageConfig = {
                            aspectRatio: targetAspect,
                            imageSize: _imageSize
                        };
                    }
                    // Determinism: lower temperature = more faithful/consistent output.
                    // temperature is a standard generationConfig field accepted by all
                    // Gemini text+image models. seed is opt-in only (LIVE-TEST per model).
                    generationConfig.temperature = (typeof opts.temperature === 'number') ? opts.temperature : 0.25;
                    if (typeof opts.seed === 'number') generationConfig.seed = opts.seed;
                    const r = await apiFetch(`${GEMINI_BASE}/${model}:generateContent`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            contents: [{ role: 'user', parts }],
                            generationConfig,
                        }),
                    });

                    if (!r.ok) {
                        const err = await r.text();
                        throw new Error(`Image API ${r.status}: ${redactSecrets(err).substring(0, 200)}`);
                    }

                    const j: GeminiResponse = await r.json();
                    for (const c of j.candidates || []) {
                        for (const p of c.content?.parts || []) {
                            if (p.inlineData?.data) return { data: p.inlineData.data, mime: p.inlineData.mimeType, engine: 'gemini', model };
                        }
                    }
                    const txt = j.candidates?.[0]?.content?.parts?.[0]?.text || JSON.stringify(j).substring(0, 200);
                    throw new Error(redactSecrets(`No image: ${txt}`));
                }, `Image (${model})`);

            } catch (e) {
                const msg = (e as Error).message || String(e);
                engineErrors.push(`Gemini ${model}: ${redactSecrets(msg).substring(0, 200)}`);
                const isRetryable = msg.includes('503') || msg.includes('UNAVAILABLE') ||
                    msg.includes('429') || msg.includes('RESOURCE_EXHAUSTED') ||
                    msg.includes('500') || msg.includes('INTERNAL') ||
                    msg.includes('400') || msg.includes('INVALID_ARGUMENT') ||
                    msg.includes('502') || msg.includes('504') ||
                    msg.includes('timed out') || msg.includes('proxy failed') ||
                    msg.includes('does not support');

                if (isRetryable && mi < IMAGE_MODELS.length - 1) {
                    log(`  ⚠️ ${model} failing — trying ${IMAGE_MODELS[mi + 1]}...`);
                    continue;
                }

                if (isRetryable) {
                    log('  ⚠️ All Gemini models failing');

                    // LAST-RESORT FALLBACKS — try whichever engine we haven't tried yet.
                    if (!useKie) {
                        try { return await kieImage(finalPrompt, imgFiles, log, shape); }
                        catch (kieErr) {
                            engineErrors.push(`KIE (last-resort): ${redactSecrets((kieErr as Error).message).substring(0, 200)}`);
                        }
                    }
                    if (!openAiFailedFallback) {
                        try { return await openAiImage(finalPrompt, imgFiles, log, shape); }
                        catch (openAiErr) {
                            engineErrors.push(`OpenAI (last-resort): ${redactSecrets((openAiErr as Error).message).substring(0, 200)}`);
                        }
                    }
                    // Smid en KONSOLIDERET fejl der viser hver motor og hvad den sagde
                    throw new Error('All engines failed:\n  • ' + engineErrors.join('\n  • '));
                }
                // Non-retryable error på det aktuelle Gemini-direkte kald — også konsolider
                throw new Error('Generation failed:\n  • ' + engineErrors.join('\n  • '));
            }
        }
        throw new Error('All image models failed');
    })();

    // Post-process til præcise pixel-dimensioner via canvas (contain-fit + hvid padding).
    // fitWithMeta preserves the engine/model provenance set above.
    return await finish(raw);
}

// ── Gemini: Veo video (image-to-video) ───────────────────────

const VIDEO_MODELS: string[] = [
    'veo-3.0-generate-preview',
    'veo-3.0-fast-generate-preview',
    'veo-2.0-generate-001'
];

interface VideoOptions {
    onProgress?: (msg: string) => void;
    aspectRatio?: string;
    duration?: string | number;
}

interface VeoOperationResponse {
    name?: string;
    done?: boolean;
    error?: { message?: string };
    response?: {
        generateVideoResponse?: {
            generatedSamples?: Array<{ video?: { uri?: string; bytesBase64Encoded?: string } }>;
        };
        videos?: Array<{ uri?: string; bytesBase64Encoded?: string }>;
        generatedVideos?: Array<{ video?: { uri?: string; bytesBase64Encoded?: string } }>;
    };
}

async function geminiVideo(prompt: string, imgFile: File | null, opts: VideoOptions = {}): Promise<VideoResult> {
    const onProgress = typeof opts.onProgress === 'function' ? opts.onProgress : () => {};
    const aspectRatioRaw = opts.aspectRatio || '1:1';
    // Veo 3 supports 16:9 and 9:16 — map 1:1 to 9:16 as fallback
    const aspectRatio = (aspectRatioRaw === '1:1') ? '9:16' : aspectRatioRaw;
    const durationSec = parseInt(String(opts.duration), 10) || 5;

    // Byg request body
    const instance: Record<string, unknown> = { prompt: prompt || '' };
    if (imgFile) {
        instance.image = {
            bytesBase64Encoded: await fileToBase64(imgFile),
            mimeType: getMime(imgFile)
        };
    }

    const body = {
        instances: [instance],
        parameters: {
            aspectRatio: aspectRatio,
            durationSeconds: durationSec,
            personGeneration: 'allow_adult',
            sampleCount: 1
        }
    };

    let lastErr: Error | null = null;
    for (const model of VIDEO_MODELS) {
        try {
            onProgress(`Starting ${model}...`);
            const startUrl = `${GEMINI_BASE}/${model}:predictLongRunning`;
            const startRes = await apiFetch(startUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });

            if (!startRes.ok) {
                const errText = await startRes.text();
                throw new Error(`Veo start ${startRes.status}: ${redactSecrets(errText).substring(0, 200)}`);
            }

            const op: VeoOperationResponse = await startRes.json();
            const opName = op.name;
            if (!opName) throw new Error('Veo: no operation name');

            // Poll indtil done — typisk 30-180s
            const startTime = Date.now();
            let pollCount = 0;
            while (true) {
                await new Promise((r) => setTimeout(r, 8000));
                pollCount++;
                const elapsed = Math.round((Date.now() - startTime) / 1000);
                onProgress(`Generating video... ${elapsed}s`);

                const pollUrl = `/api/gemini/${opName}`;
                const pollRes = await apiFetch(pollUrl);
                if (!pollRes.ok) {
                    const t = await pollRes.text();
                    throw new Error(`Veo poll ${pollRes.status}: ${redactSecrets(t).substring(0, 200)}`);
                }
                const status: VeoOperationResponse = await pollRes.json();

                if (status.error) {
                    throw new Error(redactSecrets(`Veo: ${status.error.message || JSON.stringify(status.error).substring(0, 200)}`));
                }

                if (status.done) {
                    onProgress('Fetching video...');
                    const resp = status.response || {};

                    const videoUri =
                        resp.generateVideoResponse?.generatedSamples?.[0]?.video?.uri ||
                        resp.videos?.[0]?.uri ||
                        resp.generatedVideos?.[0]?.video?.uri;

                    const inlineB64 =
                        resp.generateVideoResponse?.generatedSamples?.[0]?.video?.bytesBase64Encoded ||
                        resp.videos?.[0]?.bytesBase64Encoded ||
                        resp.generatedVideos?.[0]?.video?.bytesBase64Encoded;

                    if (inlineB64) {
                        return { data: inlineB64, mime: 'video/mp4' };
                    }
                    if (!videoUri) {
                        throw new Error(redactSecrets('Veo: no video in response — ' + JSON.stringify(resp).substring(0, 200)));
                    }

                    const dlRes = await apiFetch('/api/gemini/__url/' + encodeURIComponent(videoUri));
                    if (!dlRes.ok) throw new Error(`Veo download ${dlRes.status}`);
                    const buf = await dlRes.arrayBuffer();
                    const bytes = new Uint8Array(buf);
                    let bin = '';
                    const chunk = 0x8000;
                    for (let i = 0; i < bytes.length; i += chunk) {
                        bin += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunk)));
                    }
                    return { data: btoa(bin), mime: 'video/mp4' };
                }

                // Safety: stop polling efter 5 min
                if (pollCount > 40) throw new Error('Veo timeout (5 min)');
            }
        } catch (e) {
            lastErr = e as Error;
            const msg = (e as Error).message || String(e);
            const recoverable = /404|NOT_FOUND|model|400|INVALID_ARGUMENT|429|spending|quota|limit/i.test(msg);
            if (recoverable && model !== VIDEO_MODELS[VIDEO_MODELS.length - 1]) {
                onProgress(`${model} not available, trying next...`);
                continue;
            }
            break;
        }
    }

    throw new Error('All video models failed: ' + (lastErr?.message || 'Unknown error'));
}

// ── Gemini: tekst med retry + model fallback ─────────────────

// opts.models: the models to try, in order (default TEXT_MODELS); opts.temperature:
// the sampling temperature (default 0.4; Gemini 3 is meant to run at 1).
async function geminiText(prompt: string, imgFiles: (File | null)[] = [], opts: { models?: string[]; temperature?: number } = {}): Promise<string> {
    const parts: GeminiPart[] = [];
    for (const f of imgFiles) {
        if (f) {
            parts.push({ inlineData: { mimeType: getMime(f), data: await fileToBase64(f) } });
        }
    }
    parts.push({ text: prompt });

    const models = opts.models && opts.models.length ? opts.models : TEXT_MODELS;
    for (const model of models) {
        try {
            return await withRetry<string>(async () => {
                const r = await apiFetch(`${GEMINI_BASE}/${model}:generateContent`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ contents: [{ role: 'user', parts }], generationConfig: { temperature: opts.temperature ?? 0.4 } }),
                });

                if (!r.ok) {
                    const err = await r.text();
                    throw new Error(`Text API ${r.status}: ${err.substring(0, 200)}`);
                }

                const j: GeminiResponse = await r.json();
                if (model !== (opts.models ? models[0] : textModel)) appendLog(`  ✓ Brugte fallback model: ${model}`);
                // every text part of the answer, without the model's own thoughts
                const answer = (j.candidates?.[0]?.content?.parts || []) as { text?: string; thought?: boolean }[];
                return answer.filter((x) => x.text && !x.thought).map((x) => x.text).join('') || '';
            }, `Text (${model})`);

        } catch (e) {
            const msg = (e as Error).message || String(e);
            const isOverloaded = msg.includes('503') || msg.includes('UNAVAILABLE') ||
                                 msg.includes('429') || msg.includes('RESOURCE_EXHAUSTED') ||
                                 msg.includes('502') || msg.includes('504') ||
                                 msg.includes('timed out') || msg.includes('proxy failed');
            if (isOverloaded && model !== models[models.length - 1]) {
                appendLog(`  ⚠️ ${model} overloaded — trying next model...`);
                continue;
            }
            throw e;
        }
    }
    // Unreachable but needed for TS
    throw new Error('All text models failed');
}

// ── Pipeline execution ───────────────────────────────────────

// Background-knockout pass — takes a generated packshot and forces the
// background to pure #FFFFFF white, in case the main generation kept the
// input photo's floor/surface. The garment itself is left untouched.
// Best-effort: if the cleanup call fails, the original image is returned.
// Background whitening is intentionally a NO-OP. Forcing the background to pure
// #FFFFFF with a flood-fill created hard-edged white patches on non-uniform
// studio backgrounds, so we leave the AI's clean background untouched.
